import { DurationUnit } from "~/enums/durationUnit";
import { ObjectModelPropertyRootType } from "~/enums/objectModelPropertyType";
import {
  DataPointAggregationType,
  DataPointType,
  DataPointValueFormatType,
  VisualizationType
} from "~/enums/visualizationType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import IRepo from "~/interfaces/repo";
import { ComparisonType } from "~/models/conditionalRules/comparisonType";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import { TargetType } from "~/models/conditionalRules/targetType";
import { DatetimeMode } from "~/models/datetimeMode";
import ObjectModel from "~/models/objectModel";
import { VisualizationConditionalField } from "~/models/objectModels/objectModelConditionalFields";
import { RelationType } from "~/models/relationType";
import VisualizationDataFusionPoint from "~/models/visualizations/visualizationDataFusionPoint";
import VisualizationDataPoint from "~/models/visualizations/visualizationDataPoint";
import VisualizationDataPointConditionalFields from "~/models/visualizations/visualizationDataPointConditionalFields";
import VisualizationDataPointConditionalRuleSet from "~/models/visualizations/visualizationDataPointConditionalRuleSet";
import { IVisualizationDataPoint } from "~/models/visualizations/visualizationInterfaces";
import VisualizationRange from "~/models/visualizations/visualizationRange.ts";
import VisualizationSource from "~/models/visualizations/visualizationSource";
import VisualizationSourceForm from "~/models/visualizations/visualizationSourceForm";
import { IPeekerOptions } from "~/services/peeker";
import Repo from "~/state/modules/repo";
import store from "~/state/store";
import Form from "../../../formability/form_builder/models/form";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import { FormElementType } from "../../enums/formElementType";
import { InputFormatType } from "../../../formability/form_builder/models/inputFormatType";
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import { DataPointModel } from "../../../formability/visualizationBuilder/enums/builder-area";
import {
  DataPointStaticType,
  StaticDataPointLabels,
  StaticDataPoints,
  StaticDateDataPoints,
  StaticNumericDataPoints,
  StaticOptionDataPoints,
  StaticTextDataPoints, StaticTimeUnitDataPoints,
  StaticUserDataPoints
} from "../../../formability/visualizationBuilder/enums/data-point-static-type";
import Selected from "../../../formability/visualizationBuilder/store/modules/selected";
import LinkedRelationMappedProperty from "../objectModelProperties/linkedRelationMappedProperty";
import ObjectModelProperty from "../objectModelProperties/objectModelProperty";
import Visualization from "./visualization";
import VisualizationSourceObjectModel from "~/models/visualizations/visualizationSourceObjectModel";

interface ICustomizeDisplayOption {
  title: string;
  client_id: string;
}

export default class VisualizationSourceDataPoint extends VisualizationDataPoint implements IVisualizationDataPoint {
  readonly friendly_type = DataPointModel.dataPoint;
  header: string;
  icon = "";
  data_point_type;
  rootModel: Visualization;

  title: string;
  referenced_client_id: string;
  referenced_document_client_id: string;
  referenced_document_type: 'form' | 'objectModel';
  is_referenced: boolean;

  is_filtered: boolean;
  customize_display: boolean;
  enable_total: boolean;
  aggregation_type: DataPointAggregationType;

  temp_selected_customize_display_options = [];

  ranges: Hash<VisualizationRange> = {};

  isValidForConditionalLogic: boolean;
  referenceNameOrDefault: string;
  referenced_row_client_id: string = "";
  referenced_col_client_id: string = "";

  datetime_mode: DatetimeMode;

  data_point_conditional_rule_sets: Hash<VisualizationDataPointConditionalFields>;

  _nameInParent = "value_data_points"

  constructor(point_json, rootVisualization = null) {
    super();
    this.defineProperty('is_filtered', false);
    this.defineProperty('customize_display', false);
    this.defineProperty('enable_total', true)
    this.defineProperty('units_of_time', point_json.units_of_time || DurationUnit.minute)
    this.defineProperty("title", point_json.title)
    this.defineProperty("data_point_type", point_json.data_point_type)
    this.defineProperty("referenced_row_client_id", point_json.referenced_row_client_id || '')
    this.defineProperty("referenced_col_client_id", point_json.referenced_col_client_id || '')
    this.defineProperty('missing_data', point_json.missing_data || false);

    this.referenceNameOrDefault = point_json.title;
    this.referenced_client_id = point_json.referenced_client_id;
    this.isValidForConditionalLogic = true;
    this.rootModel = rootVisualization || Selected.visualization.get();

    const customizeOptions = Object.assign([], {parent: this, key: 'selected_customize_display_options'});
    this.defineProperty('selected_customize_display_options', TrackedArrayProxy.create(customizeOptions));

    if (point_json.source) {
      this.referenced_document_client_id = point_json.source.client_id
      this.referenced_document_type = point_json.source.rootName
    }

    const isNumRecords = this.referenced_client_id === "static_number_of_records" || this.referenced_client_id === "static_number_of_submissions"

    this.defineProperty('aggregation_type',  isNumRecords || point_json.data_point_type === DataPointType.Dimension ? DataPointAggregationType.Count : DataPointAggregationType.Sum )
    this.defineProperty('value_format_type', DataPointValueFormatType.Number)

    for (const definition of VisualizationDataPointConditionalFields.VISUALIZATION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }

    this.addToRepo();
  }

  createEmbeddedObject = {
    ranges: (parent, json) => {      
      const obj = new VisualizationRange(parent, json, this.rootModel)
      return obj;
    }
  }

  get TRACKED_ARRAYS(): string[] {
    return [
      'selected_customize_display_options'
    ];
  }

  static repo: IRepo<VisualizationSourceDataPoint> = new Repo<VisualizationSourceDataPoint>('visualizationSourceDataPoints', VisualizationSourceDataPoint, {});
  repo: IRepo<this>;

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'ranges': { type: VisualizationRange, relationType: RelationType.dictionary },
    };
    
    for (const definition of VisualizationDataPointConditionalFields.VISUALIZATION_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: VisualizationDataPointConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  get decimalPlaces(): number {
    if (this.sourceElement) {
      return this.sourceElement.decimal_places;
    }
    
    return 0;
  }

  addToRepo(): void {
    VisualizationSourceDataPoint.repo.add(this);
  }

  setRanges(ranges: VisualizationRange[]) {
    const rangesObj = ranges.reduce((obj, r) => {
      r._nameInParent = 'ranges';
      // r.rootModel = this;
      r.parent = this;
      this.trackCreationDeletion(r.client_id, null, r.asJson(), r);
      obj[r.client_id] = r;
      return obj;
    }, {})

    Object.values(this.ranges).forEach(r => {
      if (!rangesObj[r.client_id]) {
        r.destroy();
      }
    })

    this.ranges = rangesObj;
    this.parent.saveChangesToStore();
  }

  get rangesArray(): VisualizationRange[] {
    return ObjectHelper.toArray(this.ranges);
  }
  
  get type() {
    return this.friendly_type
  }

  get source() {
    if (this.referenced_document_type === 'form') {
      let f = Form.repo.find(this.referenced_document_client_id);
      if (!f) {
       f = Form.repo.findOrCreate(this.visualizationSource.source_obj_as_json_for_builder.id, this.visualizationSource.source_obj_as_json_for_builder.client_id).loadFromJson(this.visualizationSource.source_obj_as_json_for_builder);
      }
      return f;
    } else {
      let o = ObjectModel.repo.find(this.referenced_document_client_id);
      if (!o) {
        o = ObjectModel.repo.findOrCreate(this.visualizationSource.source_obj_as_json_for_builder.id, this.visualizationSource.source_obj_as_json_for_builder.client_id).loadFromJson(this.visualizationSource.source_obj_as_json_for_builder);
      }
      return o;
    }
  }

  get sources() {
    return [this.source];
  }

  get allWorkflowSteps() {
    return (this.source as Form).workflowStepsArray;
  }

  get workflowStepCompleters() {
    return (this.visualizationSource as VisualizationSourceForm).workflowStepCompleters;
  }

  get assignedToCompleters() {
    return (this.visualizationSource as VisualizationSourceForm).assignToCompleters;
  }

  get isPickList() {
    if (!this.sourceElement) return false;
    return this.sourceElement.isPickList;
  }

  get isCurrency() {
    return this.sourceElement?.format_type === InputFormatType.Currency;
  }

  get currencyType() {
    if (this.referenced_document_type === 'form') {
      return (this.sourceElement as FormElement).currency_type;
    } else {
      return '$';
    }
  }

  get sourceElement() {
    if (this.referenced_document_type === 'form') {
      return FormElement.repo.find(this.referenced_client_id);
    } else {
      return ObjectModelProperty.repo.find(this.referenced_client_id);
    }
  }

  get isNumberOfRecordsOrSubmissions(): boolean {
    return this.referenced_client_id === "static_number_of_records" || this.referenced_client_id === "static_number_of_submissions";
  }

  get elementType() {
    if (!this.sourceElement) {
      if (this.staticLabels(StaticDateDataPoints).includes(this.referenced_client_id)) {
        return FormElementType.dateTimePicker;
      }

      return FormElementType.dropdown;
    }

    return this.sourceElement.elementType;
  }

  get visualizationSource(): VisualizationSource {
    return this.rootModel.visualizationSources.find(s => {
      const obj =  s.source || s.source_obj_as_json_for_builder;
      return obj.client_id === this.referenced_document_client_id;
    });
  }

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  get originalTitle(): string {
    if (this.isCustomRowCol) {
      return "Custom Column/Row";
    }

    const dataPoint = this.visualizationSource.allMeasuresDimensionsBuiltins.find(dp => dp.referenced_client_id === this.referenced_client_id);

    if (dataPoint) {
      return dataPoint.title;
    }

    return "";
  }

  // ******************** DISPLAY HELPERS

  get isValid(): boolean {
    if (this.isCustomRowCol) return true;
    if (!this.source) return false;
    if (this.sourcesContainsDataPoint) return true; // check that object model or form exists
    if (this.referenced_client_id.includes('static')) return true; // static is always true
    if (!this.sourceElement) return false
  }

  get sourcesContainsDataPoint(): boolean {
    const rootSource = this.visualizationSource
    return rootSource.allMeasuresAndDimensions.some(s => s.referenced_client_id === this.referenced_client_id);
  }

  get shouldShowQueryFor(): boolean {
    return !this.staticLabels(StaticTimeUnitDataPoints).includes(this.referenced_client_id);
  }

  get shouldShowFormat(): boolean {
    return this.shouldShowAggregation && !this.staticLabels(StaticTimeUnitDataPoints).includes(this.referenced_client_id) && this.data_point_type !== DataPointType.Dimension;
  }

  get shouldShowUnitsOfTime(): boolean {
    return this.staticLabels(StaticTimeUnitDataPoints).includes(this.referenced_client_id);
  }

  get isCustomRowCol(): boolean {
    return this.referenced_client_id === 'static_custom_col_row';
  }

  get isAccountRelationship(): boolean {
    if (this.referenced_document_type === 'form') return false;
    return (this.visualizationSource as VisualizationSourceObjectModel).accountRelationshipDimensions?.some(dp => dp.referenced_client_id === this.referenced_client_id);
  }

  get shouldShowCustomizeDisplay(): boolean {
    return this.data_point_type !== DataPointType.Measure
      && !this.isCustomRowCol
      && !this.isValue
      && this.elementType !== FormElementType.textField
      && !this.staticLabels(StaticDateDataPoints).includes(this.referenced_client_id)
      && !this.staticLabels(StaticNumericDataPoints).includes(this.referenced_client_id)
      && !this.isDateTimeType;
  }

  get shouldShowSetRange(): boolean {
    return (this.isRow || this.isColumn) && this.data_point_type === DataPointType.Measure || (this.isDateTimeType && !this.isValue)
  }

  get shouldShowInclude(): boolean {
    return this.rootModel.visualization_type === VisualizationType.PivotTable && (this.isRow || this.isColumn);
  }

  get customizeDisplayOptions(): ICustomizeDisplayOption[] {
    if (this.data_point_type === DataPointType.Measure || this.isDateTimeType || this.staticLabels(StaticDateDataPoints).includes(this.referenced_client_id)) {
      return this.measureBuckets().map(b => ({title: b, client_id: b}));
    }

    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.assignedTo]) {
      if (this.visualizationSource) {
        return (this.visualizationSource as VisualizationSourceForm).assignToCompleters.map(completer => ({title: `${completer.first_name} ${completer.last_name}`, client_id: `${completer.first_name} ${completer.last_name}`}));
      }
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allWorkflowSteps]) {
      return (this.visualizationSource as VisualizationSourceForm).workflowStepTitles;
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allStatuses]) {      
      return this.allStatuses.map(status => ({title: status.name, client_id: status.name}))
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.workflowStepCompleter]) {
      return (this.visualizationSource as VisualizationSourceForm).workflowStepCompleters.map(completer => ({title: `${completer.first_name} ${completer.last_name}`, client_id: `${completer.first_name} ${completer.last_name}`}))
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.submittedOn]) {
      return (this.source as Form).workflowStepsArray.map(step => ({title: `${step.titleOrDefault} Submitted On`, client_id: step.client_id}))
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.completedOn]) {
      return (this.source as Form).workflowStepsArray.map(step => ({title: `${step.titleOrDefault} Completed On`, client_id: step.client_id}))
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.stepStartedOn]) {
      return (this.source as Form).workflowStepsArray.map(step => ({title: `${step.titleOrDefault} Started On`, client_id: step.client_id}))
    }
    if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.stepCompletedOn]) {
      return (this.source as Form).workflowStepsArray.map(step => ({title: `${step.titleOrDefault} Completed On`, client_id: step.client_id}))
    }

    if (this.isAccountRelationship) {
      return this.selected_customize_display_options.filter(o => o !== "").map(o => ({title: o, client_id: o}));
    }

    if (this.sourceElement && this.referenced_document_type === 'form') {
      return (this.sourceElement as FormElement).formElementsArray.map(formElement => ({title: formElement.label_title, client_id: formElement.label_title}))
    }

    if (this.sourceElement && this.referenced_document_type === 'objectModel') {
      let sourceProperty: (ObjectModelProperty | LinkedRelationMappedProperty) = (this.sourceElement as ObjectModelProperty);
      
      if (this.sourceElement.displayName === "Mapped Property") {
        sourceProperty = (this.sourceElement as ObjectModelProperty).linked_object_model_relation_properties[0];
      }

      return (sourceProperty as ObjectModelProperty).objectModelPropertiesArray.map(objectModelProperty => ({title: objectModelProperty.label_title, client_id: objectModelProperty.label_title}));
    }

    return [{title: this.title, client_id: null}];
  }

  get customizeDisplayOptionsMap() {
    return this.customizeDisplayOptions.reduce((obj, value) => {
      obj[value.client_id] = value
      return obj;
    }, {})
  }

  get selectedDisplayOptions() {
    if (!this.customize_display) return this.customizeDisplayOptions;

    if (this.isAccountRelationship) {
      return this.customizeDisplayOptions.filter(o => this.selected_customize_display_options.includes(o.client_id));
    }

    return this.customizeDisplayOptions.filter(o => !this.selected_customize_display_options.includes(o.client_id));
  }

  get isMeasure() {
    return this.data_point_type === DataPointType.Measure;
  }

  get shouldDropValue(): boolean {
    return !this.isCustomRowCol;
  }

  get isPublished(): boolean {
    return this.source.isPublished;
  }

  get allStatuses() {
    return Object.values((this.source as Form).custom_statuses);
  }

  getDataHeaders(): string[] {
    if (!this.sourceElement || this.isDateTimeType || this.data_point_type === DataPointType.Measure || this.sourceElement && this.sourceElement.getChoiceComparisonOptions().length) {
      const h = this.selectedDisplayOptions.map(c => c.title);

      if (!h.length) {
        return [""]
      }

      return h;
    }

    return [this.sourceElement.displayName];
  }

  get storeName(): string {
    return `visualizationStore`;
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = { area: this.currentBuilderArea(), modelId: this.clientIdOrId }): void {
    store.commit(`${this.storeName}/saveChanges`, { changes, peekerOptions });
  }

  getConditionalRuleSets(fieldName: string): Hash<VisualizationDataPointConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): VisualizationDataPointConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: VisualizationConditionalField): VisualizationDataPointConditionalRuleSet {
    return new VisualizationDataPointConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): VisualizationSourceDataPoint {
    return VisualizationSourceDataPoint.repo.findBy(d => d.referenced_client_id === id);
  }

  getReferencableControls(): VisualizationSourceDataPoint[] {
    return this.visualizationSource.allMeasuresAndDimensions.concat(this.visualizationSource.builtinDimensions)
      .concat(this.visualizationSource.builtinMeasures)
      .filter(dp => dp.referenced_client_id !== StaticDataPointLabels[DataPointStaticType.stepDuration])
      .map(d => {
        const dp = Object.assign({}, d);
        dp.client_id = d.referenced_client_id;
        return dp;
    });
  }

  isFusedWith(dataPoint: VisualizationDataPoint): boolean {
    if (dataPoint.friendly_type === DataPointModel.dataPoint) {
      return dataPoint.visualizationSource && this.isInTable(dataPoint) && this.rootModel.dataFusionPointsArray.some(fusion => {
        return fusion.dataPointReferencesArray.some(ref => ref.visualization_source_id === this.visualizationSource.client_id)
          && fusion.dataPointReferencesArray.some(ref => ref.visualization_source_id === dataPoint.visualizationSource.client_id);
      });
    }

    return (dataPoint as VisualizationDataFusionPoint).dataPointReferencesArray.some(ref => {
      return ref.visualization_source_id === this.visualizationSource.client_id;
    });
  }

  isInTable(point) {
    return this.referenced_client_id === point.referenced_client_id && this.referenced_document_client_id === point.referenced_document_client_id;
  }

  staticLabels(keys) {
    return keys.map(k => StaticDataPointLabels[k]);
  }

  displayConditionType(targetType: TargetType): ComparisonType {
    if (!this.sourceElement) {
      if (this.staticLabels(StaticOptionDataPoints).includes(this.referenced_client_id))  {
        return ComparisonType.singleChoice;
      }

      if (this.staticLabels(StaticTextDataPoints).includes(this.referenced_client_id))  {
        return ComparisonType.text;
      }

      if (this.staticLabels(StaticUserDataPoints).includes(this.referenced_client_id)) {
        return ComparisonType.relativeUser;
      }

      if (this.staticLabels(StaticDateDataPoints).includes(this.referenced_client_id)) {
        return ComparisonType.datetime;
      }

      if (this.staticLabels(StaticNumericDataPoints).includes(this.referenced_client_id)) {
        return ComparisonType.numeric;
      }

      if (this.isAccountRelationship) {
        return ComparisonType.accountRelationUser;
      }
    }

    return this.sourceElement.displayConditionType(targetType);
  }

  getChoiceComparisonOptions(): any[] {
    if (!this.sourceElement) {
      if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.assignedTo]) {
        return (this.visualizationSource as VisualizationSourceForm).assignToCompleters.map(completer => {
          return {label_title: `${completer.first_name} ${completer.last_name}`, client_id: `${completer.first_name} ${completer.last_name}`};
        }).concat({label_title: "Relative User", client_id: 'relative_user'});
      }

      if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.workflowStepCompleter]) {
        return (this.visualizationSource as VisualizationSourceForm).workflowStepCompleters.map(completer => {
          return {label_title: `${completer.first_name} ${completer.last_name}`, client_id: `${completer.first_name} ${completer.last_name}`};
        }).concat({label_title: "Relative User", client_id: 'relative_user'});
      }

      if (this.referenced_client_id === StaticDataPointLabels[DataPointStaticType.allWorkflowSteps]) {
        return (this.visualizationSource as VisualizationSourceForm).source_all_workflow_steps.map(step => {
          return {label_title: step.title, client_id: step.client_id};
        })
      }

      if (this.isAccountRelationship) return [];
    }

    return this.sourceElement.getChoiceComparisonOptions();
  }

  getConditionDateTimeMode(): DatetimeMode {
    return DatetimeMode.Datetime;
  }

  getConditionMinDateTime(): string {
    return '';
  }

  getConditionMaxDateTime(): string {
    return '';
  }

  targetTypes(): TargetType[] {
    return [];
  }

  get isDateTimeType(): boolean {
    return this.staticLabels(StaticDateDataPoints).includes(this.referenced_client_id)
      || (this.sourceElement as FormElement)?.element_type === FormElementType.dateTimePicker
      || (this.sourceElement as ObjectModelProperty)?.property_type === ObjectModelPropertyRootType.DateTime;
  }

  get staticDataPointType(): DataPointStaticType | null {
    for (const type of StaticDataPoints) {
      if (StaticDataPointLabels[type] === this.referenced_client_id) {
        return type;
      }
    }
    return null;
  }

}

